import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { CompensationConsultantFirm } from '../../../models/consultantFirm';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-consultant-firm-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './consultant-firm-list.component.html',
  styleUrl: './consultant-firm-list.component.scss'
})
export class ConsultantFirmListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  consultantFirms = signal<CompensationConsultantFirm[]>([]);
  isSearchVisible: boolean = false;
  matchedItems: CompensationConsultantFirm[] | undefined = undefined;
  rightItem: CompensationConsultantFirm | undefined = undefined;

  // Pagination state
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  sortBy: string = 'name';
  ascending: boolean = true;

  // Search filter state
  searchFilter: LookupTableSearchFilter = {};

  consultantFirmsColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadConsultantFirms();
  }

  loadConsultantFirms(): void {
    this.spinnerService.show();

    // Check if any search filters are active
    const hasSearchFilters = this.searchFilter.name || this.searchFilter.exactMatch;

    if (hasSearchFilters) {
      // Use search endpoint when filters are active
      const searchRequest = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        ascending: this.ascending,
        ...this.searchFilter
      };

      this.lutService.searchConsultantFirms(searchRequest).subscribe({
        next: (response) => {
          this.consultantFirms.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error: unknown) => {
          console.error('Error loading consultant firms:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Consultant Firms:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    } else {
      // Use regular paginated endpoint when no filters are active
      this.lutService.getConsultantFirmsPaginated(
        this.currentPage,
        this.pageSize,
        this.sortBy,
        this.ascending
      ).subscribe({
        next: (response) => {
          this.consultantFirms.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error: unknown) => {
          console.error('Error loading consultant firms:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Consultant Firms:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; // MatPaginator uses 0-based index
    this.pageSize = event.pageSize;
    this.loadConsultantFirms();
  }

  onSortChange(sort: Sort): void {
    // If sort direction is cleared (empty string), reset to default sort
    if (!sort.direction) {
      this.sortBy = 'name';
      this.ascending = true;
    } else {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.loadConsultantFirms();
  }

  onCreateConsultantFirm(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => {
      if (typeof value === 'boolean') {
        return value === true;
      }
      return value !== undefined && value !== null && value !== '';
    }).length;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.currentPage = 1; // Reset to first page when searching

    // Reset the paginator to first page visually
    if (this.lookupTable?.['paginator']) {
      this.lookupTable['paginator'].pageIndex = 0;
    }

    this.loadConsultantFirms();
  }

  onItemSelected(consultantFirm: CompensationConsultantFirm): void {
  }

  onReadyToMatch(event: {matchedItems?: CompensationConsultantFirm[], rightItem?: CompensationConsultantFirm}): void {
    this.matchedItems = event.matchedItems;
    this.rightItem = event.rightItem;
  }

  onEditConsultantFirm(consultantFirm: CompensationConsultantFirm): void {
  }

  onDeleteConsultantFirm(consultantFirm: CompensationConsultantFirm): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(consultantFirm.name)
      .subscribe(result => {
        if (result) {
          const originalConsultantFirms = this.consultantFirms();

          this.consultantFirms.update(consultantFirms => consultantFirms.filter(cf => cf.id !== consultantFirm.id));

          this.spinnerService.show();
          this.lutService.deleteConsultantFirm(consultantFirm.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Consultant firm deleted successfully');
              // Reload current page
              this.loadConsultantFirms();
            },
            error: (error: unknown) => {
              console.error('Error deleting consultant firm:', error);
              this.spinnerService.hide();
              this.consultantFirms.set(originalConsultantFirms);
              this.toast.error(`<strong>Failed to delete Consultant Firm:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CompensationConsultantFirm, newName: string} | {item: CompensationConsultantFirm, newValue: any, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: CompensationConsultantFirm;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.consultantFirms.update(consultantFirms =>
      consultantFirms.map(cf => cf.id === event.item.id ? updatedItem : cf)
    );

    this.spinnerService.show();
    this.lutService.updateConsultantFirm(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Consultant firm updated successfully');
        this.consultantFirms.update(consultantFirms =>
          consultantFirms.map(cf => cf.id === event.item.id ? response : cf)
        );
      },
      error: (error: unknown) => {
        console.error('Error updating consultant firm:', error);
        this.spinnerService.hide();
        this.consultantFirms.update(consultantFirms =>
          consultantFirms.map(cf => cf.id === event.item.id ? originalItem : cf)
        );
        this.toast.error(`<strong>Failed to update Consultant Firm:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newConsultantFirm: CompensationConsultantFirm = {
      id: tempId,
      name: event['name'],
      systemReserved: false
    };

    this.consultantFirms.update(consultantFirms => [newConsultantFirm, ...consultantFirms]);

    this.spinnerService.show();
    this.lutService.createConsultantFirm({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Consultant firm created successfully');
        this.consultantFirms.update(consultantFirms =>
          consultantFirms.map(cf => cf.id === tempId ? response : cf)
        );
      },
      error: (error: unknown) => {
        console.error('Error creating consultant firm:', error);
        this.spinnerService.hide();
        this.consultantFirms.update(consultantFirms =>
          consultantFirms.filter(cf => cf.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Consultant Firm:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onConsultantFirmCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalConsultantFirms = this.consultantFirms();

        this.consultantFirms.update(consultantFirms =>
          consultantFirms.filter(cf => !bulkMergeRequest.leftIds.includes(cf.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeConsultantFirmsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Consultant firms merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadConsultantFirms();
          },
          error: (error: unknown) => {
            console.error('Error merging consultant firms:', error);
            this.spinnerService.hide();
            this.consultantFirms.set(originalConsultantFirms);
            this.rightItem = originalConsultantFirms.find(cf => cf.id === bulkMergeRequest.rightId);
            this.matchedItems = originalConsultantFirms.filter(cf => bulkMergeRequest.leftIds.includes(cf.id));
            this.toast.error(`<strong>Failed to merge Consultant Firms:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
